import request from 'services/axios'

/**
 * Pending cashbox invitations (to me) + membership requests (to boxes I own).
 *
 * The path used to read `/ment/cashbox/invited-and-request-list/` — the leading
 * `cashbox_manage` had been sliced off, so this 404'd on every call.
 * `inviteCount` on the messages page was therefore permanently 0 and the
 * «درخواست عضویت» band never appeared: a user invited to a cashbox had no way
 * to learn about it from the PWA.
 */
export const getCashBoxMessages = async () => {
    const { data }: { data: { results: Array<ICashboxDetail> } } =
        await request.get(
            `/cashbox_management/cashbox/invited-and-request-list/`
        )
    return data
}

/** The number on the footer's «پیام‌ها» tab; the server stops counting at 100. */
export const getUnseenMessageCount = async () => {
    const { data }: { data: { count: number } } = await request.get(
        '/peripheral/message/unseen-count/'
    )
    return data.count
}

export interface InboxQuery {
    before?: number
    period?: TInboxPeriod
    category?: TInboxCategory
}

/**
 * One page of the inbox, newest first, optionally narrowed by time and by what
 * the message is about. Android's `request-messages-in-page-v2` pages by *day*,
 * which a filter breaks (a day can hold one matching row or none), so the PWA
 * has its own keyset endpoint and groups by day itself.
 */
export const getInboxPage = async ({ before, period, category }: InboxQuery) => {
    const params: Record<string, string> = {}
    if (before) params.before = String(before)
    if (period) params.period = period
    if (category) params.category = category
    const { data }: { data: IInboxPage } = await request.get(
        '/peripheral/message/inbox/',
        { params }
    )
    return data
}

/** «گفت‌وگوی وام»: the loan requests whose thread I am part of. */
export const getLoanThreads = async () => {
    const { data }: { data: { results: ILoanThread[] } } = await request.get(
        '/peripheral/message/loan-threads/'
    )
    return data.results
}

/**
 * Marks `lastMessageId` and every older message of the member as seen
 * (`Message.make_older_messages_seen`), so the newest id on screen is enough.
 * Answers with what is left unread, for the badge.
 */
export const makeMessagesSeen = async (lastMessageId: number) => {
    const { data }: { data: IUnseenCountReply } = await request.post(
        `/peripheral/message/make-message-seen/${lastMessageId}/`
    )
    return data?.unseen_count
}

export const deleteMessage = async (messageId: number) => {
    const { data }: { data: IUnseenCountReply } = await request.post(
        `/peripheral/message/delete-message/${messageId}/`
    )
    return data?.unseen_count
}

/** The server takes at most this many ids in one request. */
export const DELETE_IDS_LIMIT = 200

/**
 * The ticked rows. Ids that are not the member's own, or are already gone, are
 * skipped by the server, so `deleted` can be less than `ids.length`.
 */
export const deleteMessages = async (ids: readonly number[]) => {
    const { data }: { data: IDeleteMessagesReply } = await request.post(
        '/peripheral/message/delete-messages/',
        { ids }
    )
    return data
}

export interface DeleteAllQuery {
    /** The newest id on screen: a message that lands after it survives. */
    up_to: number
    period?: TInboxPeriod
    category?: TInboxCategory
}

/**
 * «همهٔ پیام‌های این فهرست»: exactly what `getInboxPage` lists for the same
 * filter — every page of it, not only the loaded ones.
 */
export const deleteAllMessages = async ({
    up_to,
    period,
    category,
}: DeleteAllQuery) => {
    const body: Record<string, string | number> = { up_to }
    if (period) body.period = period
    if (category) body.category = category
    const { data }: { data: IDeleteMessagesReply } = await request.post(
        '/peripheral/message/delete-all/',
        body
    )
    return data
}
